import type {
  CaptainSave, ServerMsg, WreckMemorial, VoyageEvent,
  HurricaneSnapshot, FogBankSnapshot,
} from '@realseas/shared';
import { Connection } from './Connection.js';
import { renderLobbyScreen } from './screens/LobbyScreen.js';
import { renderOriginScreen } from './screens/OriginScreen.js';
import { renderPortScreen } from './screens/PortScreen.js';
import { renderOutfittingScreen, resetOutfittingState } from './screens/OutfittingScreen.js';
import { renderAtSeaScreen, stopAtSeaAnimation } from './screens/AtSeaScreen.js';
import { renderIslandShopScreen } from './screens/IslandShopScreen.js';
import { renderArrivalScreen, resetArrivalState } from './screens/ArrivalScreen.js';
import { closeMapModal } from './screens/MapView.js';
import { toastForEvent } from './Toasts.js';

const TOKEN_KEY = 'realseas:token';
const NAME_KEY = 'realseas:name';

function highestLogId(log: VoyageEvent[]): number {
  let max = 0;
  for (const e of log) if (e.id > max) max = e.id;
  return max;
}

function unpackFog(flat: number[]): Set<string> {
  const set = new Set<string>();
  for (let i = 0; i < flat.length; i += 2) set.add(`${flat[i]}|${flat[i + 1]}`);
  return set;
}

function mergeFog(set: Set<string>, flat: number[]): void {
  for (let i = 0; i < flat.length; i += 2) set.add(`${flat[i]}|${flat[i + 1]}`);
}

class App {
  private container: HTMLElement;
  private conn: Connection;
  private captain: CaptainSave | null = null;
  private serverTime: number = Date.now();
  private joined: boolean = false;
  /** Discovered fog cells for this captain — populated from welcome.fogCells, merged from captain.fogDelta. */
  private fog: Set<string> = new Set();
  /** Permanent wreck memorials — full set on welcome, single new entries via memorial_added. */
  private memorials: WreckMemorial[] = [];
  /** Server-wide weather snapshot, refreshed each tick via 'weather_update'. */
  private hurricanes: HurricaneSnapshot[] = [];
  private fogBanks: FogBankSnapshot[] = [];
  /** Highest voyage-log id we've already toasted. Set on welcome (no toasts for
   *  history), then advanced as each captain update brings new entries in. */
  private lastToastedLogId: number = 0;

  constructor() {
    this.container = document.getElementById('app')!;
    this.conn = new Connection(this.onMessage.bind(this));
  }

  async start(): Promise<void> {
    try {
      await this.conn.connect();
    } catch {
      this.showError('Could not reach the harbormaster. Try again.');
      return;
    }
    // Auto-rejoin if we have a saved token+name. Avoids the "click Continue
    // Voyage every time you refresh" friction. If the server doesn't recognise
    // the token (e.g. the DB was reset), it'll fall back to creating a new
    // captain with the same name; the welcome message will replace the token.
    const savedToken = localStorage.getItem(TOKEN_KEY) || '';
    const savedName = localStorage.getItem(NAME_KEY) || '';
    if (savedToken && savedName) {
      this.container.innerHTML = `
        <p style="text-align: center; margin-top: 80px; font-style: italic; color: var(--ink-faint);">
          Recovering ship's log…
        </p>`;
      this.conn.send({ type: 'join', token: savedToken, username: savedName });
      return;
    }
    this.showLobby();
  }

  private showLobby(): void {
    const savedToken = localStorage.getItem(TOKEN_KEY) || '';
    const savedName = localStorage.getItem(NAME_KEY) || '';
    renderLobbyScreen(this.container, this.conn, savedToken, savedName);
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case 'welcome':
        localStorage.setItem(TOKEN_KEY, msg.token);
        localStorage.setItem(NAME_KEY, msg.captain.username);
        this.captain = msg.captain;
        this.serverTime = msg.serverTime;
        this.joined = true;
        this.fog = unpackFog(msg.fogCells);
        this.memorials = msg.memorials ?? [];
        this.hurricanes = msg.hurricanes ?? [];
        this.fogBanks = msg.fogBanks ?? [];
        // Anchor the toast watermark to the latest log entry we already know
        // about, so reconnect catch-ups don't dump 50 toasts in one go.
        this.lastToastedLogId = highestLogId(msg.captain.voyageLog);
        this.render();
        return;
      case 'captain':
        this.fireToastsForNewLogs(msg.captain.voyageLog);
        this.captain = msg.captain;
        this.serverTime = msg.serverTime;
        if (msg.fogDelta && msg.fogDelta.length > 0) {
          mergeFog(this.fog, msg.fogDelta);
        }
        this.render();
        return;
      case 'memorial_added':
        this.memorials.push(msg.memorial);
        this.render();
        return;
      case 'weather_update':
        this.hurricanes = msg.hurricanes;
        this.fogBanks = msg.fogBanks;
        this.render();
        return;
      case 'weather_event':
        // Flavor — just shown as a transient banner. Nothing to render.
        this.showError(msg.text);
        return;
      case 'retired':
        // The captain settled ashore; the save is gone. Forget the token and
        // go back to the very first screen so a new voyage can begin.
        this.forgetCaptain();
        return;
      case 'error':
        this.showError(msg.reason);
        // If auto-rejoin failed (e.g. name conflict after DB reset), fall back
        // to the lobby so the captain can try a different name.
        if (!this.joined) this.showLobby();
        return;
    }
  }

  /** Drop all local state for the current captain and show the lobby fresh. */
  private forgetCaptain(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    stopAtSeaAnimation();
    closeMapModal();
    resetOutfittingState();
    resetArrivalState();
    this.captain = null;
    this.joined = false;
    this.fog = new Set();
    this.lastToastedLogId = 0;
    this.showLobby();
  }

  private render(): void {
    if (!this.joined || !this.captain) return;
    const c = this.captain;
    if (c.status !== 'at_sea') stopAtSeaAnimation();
    if (c.status !== 'outfitting') resetOutfittingState();
    if (c.status !== 'arrived') resetArrivalState();
    if (c.status === 'lobby' || c.originId === null) {
      renderOriginScreen(this.container, this.conn, c);
    } else if (c.status === 'picking_port' || c.status === 'picking_destination') {
      renderPortScreen(this.container, this.conn, c);
    } else if (c.status === 'outfitting') {
      renderOutfittingScreen(this.container, this.conn, c);
    } else if (c.status === 'at_sea') {
      renderAtSeaScreen(this.container, this.conn, c, this.serverTime, this.fog, this.memorials, this.hurricanes, this.fogBanks);
    } else if (c.status === 'at_island') {
      renderIslandShopScreen(this.container, this.conn, c);
    } else if (c.status === 'arrived') {
      renderArrivalScreen(this.container, this.conn, c, this.serverTime);
    } else if (c.status === 'lost') {
      renderAtSeaScreen(this.container, this.conn, c, this.serverTime, this.fog, this.memorials, this.hurricanes, this.fogBanks);
    } else {
      this.container.textContent = `Status: ${c.status}`;
    }
  }

  private fireToastsForNewLogs(log: VoyageEvent[]): void {
    // The server returns voyage_log newest-first; iterate oldest-first so toasts
    // stack in chronological order on screen.
    const fresh = log.filter(e => e.id > this.lastToastedLogId);
    fresh.sort((a, b) => a.happenedAt - b.happenedAt);
    for (const e of fresh) toastForEvent(e);
    if (fresh.length > 0) this.lastToastedLogId = highestLogId(log);
  }

  private showError(reason: string): void {
    const banner = document.getElementById('error-banner')!;
    banner.textContent = reason;
    banner.hidden = false;
    window.clearTimeout((banner as unknown as { _t?: number })._t);
    (banner as unknown as { _t?: number })._t = window.setTimeout(() => { banner.hidden = true; }, 4000);
  }
}

new App().start();
